/* eslint-disable sort-keys */
import React, { useEffect, useState } from 'react';
import { useLocalization, useTranslate } from '@im/react-localization';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { usePNAforWarehouse } from '../use-pna-warehouse';
import { WarehouseDetails } from './common.types';
import { TableSkeleton } from '@im/ui-lib/TableSkeleton';
import { useDecodedToken } from 'mf_core/auth';
import WarehouseGridData from './WarehouseGridData';
import WarehouseINGridData from './WarehouseINGridData';

type WarehouseDetailsProps = {
  brazilReqProps?: any;
  fromPage: string;
  id: any;
  licenseSelectorVendorSubrange?: string;
  onClose: () => void;
  open: boolean;
  reserveFlag: boolean;
  sku: string;
  skuType?: string;
  vendorCode?: string;
  warehouseDetails: WarehouseDetails[];
};

export function WarehouseDetailsModal(props: WarehouseDetailsProps) {
  const {
    brazilReqProps,
    fromPage,
    licenseSelectorVendorSubrange,
    onClose,
    open,
    sku,
    skuType,
    vendorCode,
    warehouseDetails,
  } = props;
  const translate = useTranslate();
  const { formatDate } = useLocalization();
  const decodedToken = useDecodedToken();
  const siteCode = decodedToken?.siteCode;
  const [warehouseStockDetailUpdate, setwarehouseStockDetailUpdate] = useState<
    WarehouseDetails[]
  >([]);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const { data, isLoading } = usePNAforWarehouse(
    sku,
    skuType,
    vendorCode,
    licenseSelectorVendorSubrange,
    brazilReqProps
  );
  const warehouseProductDetails = data?.[0].warehouseList;
  const inWarehouseColumnFlag = data?.[0].warehouseColumn;

  const sortFunction = (a: any, b: any) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateA > dateB ? 1 : -1;
  };

  useEffect(() => {
    if (isInitialRender && data !== undefined) {
      setIsInitialRender(false);
      if (warehouseProductDetails) {
        const warehouseStockList = warehouseProductDetails.map(
          (warehouse: any) => {
            return {
              estimatedLeadTime: warehouse.leadTimeEta,
              etaDate: warehouse.etaDate,
              id: warehouse.warehousseId,
              name:
                warehouse.warehouseDescription ?? warehouse.warehouseLocation,
              onOrderQuantity: warehouse.onOrderQuantity,
              quantityAvailable: warehouse.warehouseAvailableQuantity,
              quantityOnOrder:
                warehouse.quantityOnOrder !== undefined &&
                warehouse.quantityOnOrder !== null &&
                warehouse.quantityOnOrder?.length > 0 !== null &&
                warehouse.quantityOnOrder
                  ?.slice()
                  .sort((a: any, b: any) => sortFunction(a, b))
                  .map((qtyOrderList: any, i: number) => (
                    <>
                      <tr key={i}>
                        <td>{qtyOrderList.quantity || 0}</td>{' '}
                      </tr>
                    </>
                  )),
              quantityOnOrderEta:
                warehouse.quantityOnOrder !== undefined &&
                warehouse.quantityOnOrder !== null &&
                warehouse.quantityOnOrder?.length > 0 !== null &&
                warehouse.quantityOnOrder
                  ?.slice()
                  .sort((a: any, b: any) => sortFunction(a, b))
                  .map((qtyOrderList: any, i: number) => (
                    <>
                      <tr key={i}>
                        <td>{formatDate(qtyOrderList.date) || '-'}</td>
                      </tr>
                    </>
                  )),
              warehouseLocation: warehouse.warehouseLocation,
              price: warehouse?.taxFields?.unitPriceWithTax ?? 0,
            } as WarehouseDetails;
          }
        );
        setwarehouseStockDetailUpdate(warehouseStockList);
      }
    }
  }, [
    data,
    formatDate,
    fromPage,
    isInitialRender,
    siteCode,
    warehouseDetails,
    warehouseProductDetails,
    warehouseStockDetailUpdate,
  ]);

  return (
    <>
      <Modal onClose={onClose} open={open}>
        {isLoading ? (
          <RootBox>
            <TableSkeleton
              columns={[
                '----------------------------------------------------------------------------',
                '--------------------',
                '-------------------------------------------------',
                '--------------------',
                '------------------------------------',
                '----------------------------------------------------------',
              ]}
              key={sku}
              rows={4}
            />
          </RootBox>
        ) : (
          <RootBox>
            <Typography
              sx={{
                display: 'flex',
                fontSize: '19px',
                justifyContent: 'space-between',
              }}
              variant="h5"
            >
              {siteCode?.toUpperCase() !== 'IN'
                ? translate('Warehouse.ETAPopupName')
                : translate('Warehouse.InTitle')}
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>{' '}
            </Typography>

            {siteCode?.toUpperCase() !== 'IN' ? (
              <>
                <WarehouseGridData
                  fromPage={fromPage}
                  siteCode={siteCode}
                  warehouseStockDetailUpdate={warehouseStockDetailUpdate}
                ></WarehouseGridData>
              </>
            ) : (
              <>
                <WarehouseINGridData
                  inWarehouseColumnFlag={inWarehouseColumnFlag}
                  warehouseStockDetailUpdate={warehouseStockDetailUpdate}
                ></WarehouseINGridData>
              </>
            )}
          </RootBox>
        )}
      </Modal>
    </>
  );
}

const RootBox = styled(Box)(({ theme }: { theme: any }) => ({
  background: theme.palette.background.paper,
  borderRadius: 5,
  boxShadow: '24px solid #000',
  display: 'flex',
  flexDirection: 'column',
  left: '50%',
  outline: 'none',
  padding: theme.spacing(3),
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'auto',
}));
